import React, { useState, useEffect } from "react";
import { apiErrorText } from '../lib/apiError';
import api from "../utils/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Calendar, Clock, Anchor, TrendingUp, CheckCircle, AlertCircle, Fuel, Phone, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API_URL } from '../config/api';

const API = API_URL;

const FUEL_LEVELS = [
  { value: "empty", label: "Empty", color: "bg-red-500" },
  { value: "1/4", label: "1/4 Tank", color: "bg-orange-500" },
  { value: "1/2", label: "1/2 Tank", color: "bg-yellow-500" },
  { value: "3/4", label: "3/4 Tank", color: "bg-lime-500" },
  { value: "full", label: "Full", color: "bg-green-500" },
  { value: "unknown", label: "Unknown", color: "bg-gray-400" }
];

const TeamMemberDashboard = ({ teamMemberId, teamMemberData }) => {
  const [trips, setBookings] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFuelDialog, setShowFuelDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [newFuelLevel, setNewFuelLevel] = useState("");
  const [activeTimeEntry, setActiveTimeEntry] = useState(null);
  const [clockInLoading, setClockInLoading] = useState(false);
  const [stats, setStats] = useState({
    upcoming: 0,
    completed: 0,
    thisWeek: 0
  });

  useEffect(() => {
    fetchData();
    checkActiveTimeEntry();
  }, [teamMemberId]);

  // Update elapsed time every minute if clocked in
  useEffect(() => {
    if (!activeTimeEntry) return;
    
    const interval = setInterval(() => {
      // Force re-render to update elapsed time
      setActiveTimeEntry(prev => ({ ...prev }));
    }, 60000); // Every minute
    
    return () => clearInterval(interval);
  }, [activeTimeEntry]);

  const checkActiveTimeEntry = async () => {
    try {
      const response = await axios.get(`${API}/time-entries/active/${teamMemberId}`);
      setActiveTimeEntry(response.data);
    } catch (error) {
      // No active entry is fine
      setActiveTimeEntry(null);
    }
  };

  const handleClockIn = async () => {
    try {
      setClockInLoading(true);
      const response = await axios.post(`${API}/time-entries/clock-in`, {
        teamMemberId: teamMemberId
      });
      setActiveTimeEntry(response.data);
      toast.success(`Clocked in at ${new Date().toLocaleTimeString()}`);
    } catch (error) {
      toast.error(apiErrorText(error, 'Failed to clock in'));
    } finally {
      setClockInLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!activeTimeEntry) return;
    
    try {
      setClockInLoading(true);
      const response = await axios.post(`${API}/time-entries/clock-out`, {
        timeEntryId: activeTimeEntry.id
      });
      toast.success(`Clocked out! Hours worked: ${response.data.hoursWorked}`);
      setActiveTimeEntry(null);
    } catch (error) {
      toast.error(apiErrorText(error, 'Failed to clock out'));
    } finally {
      setClockInLoading(false);
    }
  };

  const getElapsedTime = () => {
    if (!activeTimeEntry?.clockInTime) return '0:00';
    
    const clockIn = new Date(activeTimeEntry.clockInTime);
    const now = new Date();
    const elapsed = Math.floor((now - clockIn) / 1000); // seconds
    
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tripsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/trips`),
        axios.get(`${API}/assets`)
      ]);

      const allBookings = tripsRes.data;
      const today = new Date().toISOString().split('T')[0];

      // Filter teamMember's trips
      const myBookings = allBookings.filter(t => t.teamMemberId === teamMemberId);
      const upcoming = myBookings.filter(t => t.date >= today).sort((a, b) => {
        if (a.date === b.date) return a.startTime.localeCompare(b.startTime);
        return a.date.localeCompare(b.date);
      });
      const completed = myBookings.filter(t => t.date < today);

      // This week trips
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      const nextWeekStr = nextWeek.toISOString().split('T')[0];
      const thisWeek = upcoming.filter(t => t.date <= nextWeekStr);

      setBookings(upcoming);
      setAssets(assetsRes.data);
      setStats({
        upcoming: upcoming.length,
        completed: completed.length,
        thisWeek: thisWeek.length
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getAssetDetails = (assetId) => {
    return assets.find(b => b.id === assetId);
  };

  const handleOpenFuelEdit = (asset) => {
    setSelectedAsset(asset);
    setNewFuelLevel(asset.fuelLevel || "unknown");
    setShowFuelDialog(true);
  };

  const handleUpdateFuel = async () => {
    if (!newFuelLevel) {
      toast.error("Please select a fuel level");
      return;
    }

    try {
      await axios.patch(`${API}/assets/${selectedAsset.id}/fuel?fuelLevel=${newFuelLevel}`);
      toast.success("Fuel level updated successfully!");
      setShowFuelDialog(false);
      fetchData(); // Refresh to show new fuel level
    } catch (error) {
      console.error("Error updating fuel:", error);
      toast.error("Failed to update fuel level");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getTimeUntilBooking = (date, time) => {
    const tripDateTime = new Date(`${date}T${time}`);
    const now = new Date();
    const diff = tripDateTime - now;
    
    if (diff < 0) return "Past";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    return "Soon";
  };

  const nextBooking = trips[0];
  const upcomingBookings = trips.slice(1, 6);

  if (loading) {
    return <div className="text-center py-12">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800">
          Welcome back, {teamMemberData?.name}!
        </h2>
        <p className="text-gray-600 mt-1">Here's your schedule overview</p>
      </div>

      {/* Clock In/Out Section */}
      <Card className={`border ${activeTimeEntry ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${activeTimeEntry ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Clock className={`w-6 h-6 ${activeTimeEntry ? 'text-green-700' : 'text-gray-600'}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {activeTimeEntry ? 'Clocked In' : 'Ready to Start?'}
                </h3>
                {activeTimeEntry ? (
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">
                      Started at {new Date(activeTimeEntry.clockInTime).toLocaleTimeString()}
                    </p>
                    <p className="text-sm font-semibold text-green-700">
                      Time Elapsed: {getElapsedTime()}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Clock in to start tracking your hours</p>
                )}
              </div>
            </div>
            <div>
              {activeTimeEntry ? (
                <Button
                  onClick={handleClockOut}
                  disabled={clockInLoading}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {clockInLoading ? 'Processing...' : 'Clock Out'}
                </Button>
              ) : (
                <Button
                  onClick={handleClockIn}
                  disabled={clockInLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {clockInLoading ? 'Processing...' : 'Clock In'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Booking - Prominent Display */}
      {nextBooking ? (
        <Card className="border border-blue-500 bg-gradient-to-br from-blue-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Anchor className="w-5 h-5 text-blue-600" />
              Your Next Booking
              <Badge className="ml-auto bg-blue-600">
                {getTimeUntilBooking(nextBooking.date, nextBooking.startTime)}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Booking Details */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Date & Time</p>
                  <p className="text-xl font-bold text-gray-800">
                    {formatDate(nextBooking.date)}
                  </p>
                  <p className="text-lg text-gray-700">
                    {formatTime(nextBooking.startTime)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Duration</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {nextBooking.duration} minutes
                  </p>
                </div>

                {nextBooking.notes && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Special Instructions</p>
                    <p className="text-sm text-gray-700 bg-yellow-50 p-3 rounded border border-yellow-200">
                      {nextBooking.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Asset Details */}
              {nextBooking.assetId && getAssetDetails(nextBooking.assetId) && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 mb-3">Your Asset</p>
                  {(() => {
                    const asset = getAssetDetails(nextBooking.assetId);
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Anchor className="w-5 h-5 text-blue-600" />
                          <p className="text-xl font-bold text-gray-800">{asset.name}</p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge variant={asset.type === 'double-decker' ? 'default' : 'secondary'}>
                            {asset.type === 'double-decker' ? '🚢 Double-Decker' : '⛵ Single-Decker'}
                          </Badge>
                        </div>

                        <div>
                          <p className="text-sm text-gray-600 mb-1">Fuel Level</p>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                              asset.fuelLevel === 'full' ? 'bg-green-500' :
                              asset.fuelLevel === 'half' ? 'bg-yellow-500' :
                              asset.fuelLevel === 'low' ? 'bg-orange-500' :
                              'bg-red-500'
                            }`} />
                            <span className="text-sm font-medium capitalize">
                              {asset.fuelLevel || 'Unknown'}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="ml-2"
                              onClick={() => handleOpenFuelEdit(asset)}
                            >
                              <Fuel className="w-3 h-3 mr-1" />
                              Update
                            </Button>
                          </div>
                        </div>

                        {asset.notes && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-gray-600 mb-1">Asset Notes:</p>
                            <p className="text-sm text-gray-700">{asset.notes}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-dashed">
          <CardContent className="pt-6 pb-6 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No upcoming trips scheduled</p>
            <p className="text-sm text-gray-500 mt-1">Check back later for new assignments</p>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Bookings List */}
      {upcomingBookings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Upcoming Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingBookings.map((trip) => {
                const asset = getAssetDetails(trip.assetId);
                return (
                  <div
                    key={trip.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="font-semibold text-gray-800">
                          {formatDate(trip.date)}
                        </p>
                        <p className="text-gray-600">{formatTime(trip.startTime)}</p>
                        <Badge variant="outline" className="text-xs">
                          {trip.duration}min
                        </Badge>
                      </div>
                      {asset && (
                        <p className="text-sm text-gray-600">
                          <Anchor className="w-3 h-3 inline mr-1" />
                          {asset.name}
                        </p>
                      )}
                      {trip.fromBooking && trip.customerName && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs font-semibold text-blue-900 mb-1">Customer Contact:</p>
                          <div className="flex flex-col gap-1 text-xs text-gray-700">
                            <span className="font-medium">{trip.customerName}</span>
                            <a href={`tel:${trip.customerPhone}`} className="text-blue-600 hover:underline">
                              📞 {trip.customerPhone}
                            </a>
                            <a href={`mailto:${trip.customerEmail}`} className="text-blue-600 hover:underline">
                              ✉️ {trip.customerEmail}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                    <Badge className="bg-blue-100 text-blue-700">
                      {getTimeUntilBooking(trip.date, trip.startTime)}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Emergency Contacts */}
      <Card className="border border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-900">
            <Phone className="w-5 h-5" />
            Emergency Contacts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <a
              href="tel:911"
              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-red-300 hover:border-red-500 transition-all"
            >
              <div className="p-2 bg-red-100 rounded-lg">
                <Phone className="w-5 h-5 text-red-700" />
              </div>
              <div>
                <div className="font-bold text-red-900">911</div>
                <div className="text-sm text-gray-600">Emergency Services</div>
              </div>
            </a>
            <a
              href="tel:512-974-6700"
              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-300 hover:border-blue-500 transition-all"
            >
              <div className="p-2 bg-blue-100 rounded-lg">
                <Phone className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <div className="font-bold text-gray-900">Lake Austin Fire</div>
                <div className="text-sm text-gray-600">(512) 974-6700</div>
              </div>
            </a>
            <a
              href="tel:512-266-1336"
              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-300 hover:border-blue-500 transition-all"
            >
              <div className="p-2 bg-blue-100 rounded-lg">
                <Anchor className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <div className="font-bold text-gray-900">Flag Ship Towing</div>
                <div className="text-sm text-gray-600">(512) 266-1336</div>
              </div>
            </a>
            <a
              href="tel:512-389-4800"
              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-300 hover:border-blue-500 transition-all"
            >
              <div className="p-2 bg-green-100 rounded-lg">
                <Phone className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <div className="font-bold text-gray-900">TX Parks & Wildlife</div>
                <div className="text-sm text-gray-600">(512) 389-4800</div>
              </div>
            </a>
          </div>
          <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-900 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>For full emergency information and additional contacts, visit the Emergency tab in the sidebar.</span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Fuel Update Dialog */}
      <Dialog open={showFuelDialog} onOpenChange={setShowFuelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Fuel Level</DialogTitle>
          </DialogHeader>
          {selectedAsset && (
            <div className="space-y-4 mt-4">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm font-medium text-blue-900">
                  {selectedAsset.name}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {selectedAsset.type}
                </p>
              </div>

              <div>
                <Label>Current Fuel Level *</Label>
                <Select value={newFuelLevel} onValueChange={setNewFuelLevel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select fuel level" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_LEVELS.map(fl => (
                      <SelectItem key={fl.value} value={fl.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${fl.color}`} />
                          {fl.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowFuelDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateFuel}>
                  <Fuel className="w-4 h-4 mr-2" />
                  Update Fuel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamMemberDashboard;
